import {
  FeedbackBreadcrumbKind as GeneratedFeedbackBreadcrumbKind,
  FeedbackBreadcrumbOutcome as GeneratedFeedbackBreadcrumbOutcome,
  FeedbackFeedEndpoint as GeneratedFeedbackFeedEndpoint,
  FeedbackTrigger as GeneratedFeedbackTrigger,
} from '@/lib/api/generated';
import type {
  FeedbackBreadcrumb as GeneratedFeedbackBreadcrumb,
  FeedbackScene as GeneratedFeedbackScene,
  FeedbackTarget as GeneratedFeedbackTarget,
  PublicDocId,
  SubmitFeedbackRequest as GeneratedSubmitFeedbackRequest,
  SubmitFeedbackResponse as GeneratedSubmitFeedbackResponse,
} from '@/lib/api/generated';

// Defines the client-owned feedback vocabulary and ergonomic constructors. Wire shapes and
// server-owned discriminants come from the generated HTTP contract; action and source values
// remain local because the backend deliberately stores those detail fields without interpreting
// or restricting them.

/** Enumerates the scene identifiers shared across the feedback ontology. */
export const FEEDBACK_SCENE_KIND = {
  DOC_THREAD: 'doc_thread',
  HOME_FEED: 'home_feed',
  PRODUCT_PAGE: 'product_page',
  GUIDE_PAGE: 'guide_page',
  UNKNOWN: 'unknown',
} as const satisfies Record<string, GeneratedFeedbackScene['kind']>;

/** Describes the allowed scene identifiers. */
export type FeedbackSceneKind = GeneratedFeedbackScene['kind'];

/** Enumerates the home-feed endpoint identifiers used by feedback scenes. */
export const FEEDBACK_HOME_FEED_ENDPOINT = {
  PUBLIC: GeneratedFeedbackFeedEndpoint.public,
  UNIFIED: GeneratedFeedbackFeedEndpoint.unified,
} as const;

/** Describes the allowed home-feed endpoint identifiers. */
export type FeedbackHomeFeedEndpoint = GeneratedFeedbackFeedEndpoint;

/** Enumerates the target identifiers shared across the feedback ontology. */
export const FEEDBACK_TARGET_KIND = {
  DOC: 'doc',
  FEED_ITEM: 'feed_item',
  PRODUCT: 'product',
  TOPIC: 'topic',
} as const satisfies Record<string, GeneratedFeedbackTarget['kind']>;

/** Describes the allowed target identifiers. */
export type FeedbackTargetKind = GeneratedFeedbackTarget['kind'];

/** Enumerates the breadcrumb event identifiers shared across the feedback ontology. */
export const FEEDBACK_BREADCRUMB_KIND = {
  NAVIGATION: GeneratedFeedbackBreadcrumbKind.navigation,
  ACTION: GeneratedFeedbackBreadcrumbKind.action,
  NOTIFICATION: GeneratedFeedbackBreadcrumbKind.notification,
  ERROR: GeneratedFeedbackBreadcrumbKind.error,
} as const;

/** Describes the allowed breadcrumb event identifiers. */
export type FeedbackBreadcrumbKind = GeneratedFeedbackBreadcrumbKind;

/** Enumerates the action outcomes shared across the feedback ontology. */
export const BREADCRUMB_OUTCOME = {
  PENDING: GeneratedFeedbackBreadcrumbOutcome.pending,
  SUCCESS: GeneratedFeedbackBreadcrumbOutcome.success,
  FAILURE: GeneratedFeedbackBreadcrumbOutcome.failure,
} as const;

/** Enumerates the action vocabulary feedback can express. */
export const FEEDBACK_ACTION = {
  POST_MESSAGE: 'post_message',
  POST_QUESTION: 'post_question',
  POST_SUGGESTED_QUESTION: 'post_suggested_question',
  REQUEST_AI_REPLY: 'request_ai_reply',
  UPVOTE: 'upvote',
  DOWNVOTE: 'downvote',
  RETRACT_VOTE: 'retract_vote',
  SAVE_POST: 'save_post',
  UNSAVE_POST: 'unsave_post',
  SAVE_PRODUCT: 'save_product',
  UNSAVE_PRODUCT: 'unsave_product',
  ADD_PRODUCT_BY_URL: 'add_product_by_url',
  FOLLOW_USER: 'follow_user',
  UNFOLLOW_USER: 'unfollow_user',
  FOLLOW_TOPIC: 'follow_topic',
  UNFOLLOW_TOPIC: 'unfollow_topic',
  CREATE_INVITE: 'create_invite',
  LOG_IN: 'log_in',
  CREATE_ACCOUNT: 'create_account',
  START_OAUTH_SIGN_IN: 'start_oauth_sign_in',
  COMPLETE_OAUTH_SIGN_IN: 'complete_oauth_sign_in',
  LOG_OUT: 'log_out',
  CHANGE_PASSWORD: 'change_password',
  START_EMAIL_CHANGE: 'start_email_change',
  RESEND_EMAIL_VERIFICATION: 'resend_email_verification',
  CANCEL_EMAIL_CHANGE: 'cancel_email_change',
  REQUEST_PASSWORD_RESET: 'request_password_reset',
  RESET_PASSWORD: 'reset_password',
  UPDATE_AVATAR: 'update_avatar',
  UPDATE_MARKETPLACE_PREFERENCES: 'update_marketplace_preferences',
  UPDATE_PROFILE_DISCOVERABILITY: 'update_profile_discoverability',
  REMOVE_OAUTH_CONNECTION: 'remove_oauth_connection',
  DELETE_ACCOUNT: 'delete_account',
} as const;

/** Describes the allowed feedback action identifiers. */
export type FeedbackAction = (typeof FEEDBACK_ACTION)[keyof typeof FEEDBACK_ACTION];

/** Enumerates the UI surfaces that originate an action breadcrumb (`detail.source`). */
export const FEEDBACK_SOURCE = {
  COLLECTIONS: 'collections',
  LIVE_CONTEXT: 'live_context',
  PRODUCT_MINI_CARD: 'product_mini_card',
  PRODUCT_PAGE: 'product_page',
  FOLLOW_BUTTON_DIRECT: 'follow_button_direct',
  FOLLOW_BUTTON_MODAL: 'follow_button_modal',
} as const;

/** Describes the allowed action-source identifiers. */
export type FeedbackSource = (typeof FEEDBACK_SOURCE)[keyof typeof FEEDBACK_SOURCE];

/**
 * Describes the bounded, PII-free detail payload carried by an action breadcrumb.
 *
 * This is the ontology-owned contract for `Breadcrumb.detail` on action events: the
 * breadcrumb helpers stamp `action`, and callers may attach the known structured keys
 * below rather than arbitrary records, so the shape stays consistent across producers.
 */
export type ActionDetail = {
  /** Identifies the action contract the breadcrumb describes; stamped by the breadcrumb helpers. */
  action?: FeedbackAction;
  /** Identifies the UI surface that originated the action. */
  source?: FeedbackSource;
  /** Identifies the OAuth provider involved in the action, when applicable. */
  provider?: string;
  /** Stores a bounded, PII-free failure reason for a failed action. */
  error?: string;
};

/** Rust-owned description of the surface the user was on when they opened feedback. */
export type Scene = GeneratedFeedbackScene;

/** Rust-owned feedback target, or null when the report concerns only its current scene. */
export type Target = GeneratedFeedbackTarget | null;

/** Rust-owned breadcrumb event captured before the user submitted feedback. */
export type Breadcrumb = GeneratedFeedbackBreadcrumb;

/** Enumerates the trigger identifiers shared across the feedback ontology. */
export const FEEDBACK_TRIGGERS = {
  DOWNVOTE_AUTO: GeneratedFeedbackTrigger.downvote_auto,
  FLAG: GeneratedFeedbackTrigger.flag,
  MENU: GeneratedFeedbackTrigger.menu,
} as const;

/** Describes the allowed trigger identifiers. */
export type FeedbackTrigger = GeneratedFeedbackTrigger;

/**
 * Complete generated submission with an explicit target decision.
 *
 * The HTTP field is optional for compatibility with deserializers, but the feedback controller
 * always captures either a concrete target or null. Requiring the field here prevents client code
 * from accidentally skipping that decision without redefining the rest of the wire request.
 */
export type FeedbackSubmission = Omit<GeneratedSubmitFeedbackRequest, 'target'> & {
  target: GeneratedFeedbackTarget | null;
};

/** Generated response returned after feedback submission. */
export type FeedbackSubmitResponse = GeneratedSubmitFeedbackResponse;

/** Fails exhaustive unions when a caller reaches an unsupported variant. */
export function assertNever(x: never): never {
  throw new Error('Unexpected value: ' + JSON.stringify(x));
}

/** Builds a document-thread scene for the given public thread identifier. */
export function sceneDocThread(thread_id: PublicDocId, main_topic: string | null): Scene {
  return { kind: FEEDBACK_SCENE_KIND.DOC_THREAD, thread_id, main_topic };
}

/** Builds a home-feed scene for the current authenticated or public endpoint. */
export function sceneHomeFeed(endpoint: FeedbackHomeFeedEndpoint): Scene {
  return { kind: FEEDBACK_SCENE_KIND.HOME_FEED, endpoint };
}

/** Builds an offer-page scene for the given offer reference. */
export function sceneOfferPage(product_ref: string): Scene {
  return { kind: FEEDBACK_SCENE_KIND.PRODUCT_PAGE, product_ref };
}

/**
 * Builds the guide-page feedback scene from the route topic.
 *
 * `topic_key` is the raw decoded route topic (what the user navigated to), not a
 * resolved/canonical concept key. A feedback scene only needs immediate, stable
 * context for a report; resolving to a canonical `cdef.key` would add an async
 * dependency and a brief window with no guide context, for a grouping benefit we
 * don't currently need. Revisit if feedback ever needs canonical topic grouping.
 */
export function sceneGuidePage(topic_key: string): Scene {
  return { kind: FEEDBACK_SCENE_KIND.GUIDE_PAGE, topic_key };
}

/** Builds the fallback scene used when no tracked route matches. */
export function sceneUnknown(): Scene {
  return { kind: FEEDBACK_SCENE_KIND.UNKNOWN };
}

/** Builds a document-thread target for feedback opened from a doc surface. */
export function targetDoc(id: PublicDocId): Target {
  return { kind: FEEDBACK_TARGET_KIND.DOC, id };
}

/** Builds a feed-item target for feedback opened from a feed surface. */
export function targetFeedItem(id: PublicDocId): Target {
  return { kind: FEEDBACK_TARGET_KIND.FEED_ITEM, id };
}

/** Builds an offer target for feedback opened from an offer surface. */
export function targetOffer(product_ref: string): Target {
  return { kind: FEEDBACK_TARGET_KIND.PRODUCT, product_ref };
}

/** Builds a topic target for feedback opened from a guide surface. */
export function targetTopic(topic_key: string): Target {
  return { kind: FEEDBACK_TARGET_KIND.TOPIC, topic_key };
}
